import { FC, useContext } from 'react';
import './BundleCard.css';
import { resolveImageSrc } from '../../api/client';
import { formatDollars } from '../../shared/functions/formatter';
import { CashierContext } from '../Cashier';

type BundleCardProps = {
  bundle: CatalogBundle;
  /** Item details from the visible catalog, for summaries and tile shortcuts. */
  itemsById: Map<number, Item>;
  /** Category name for each item id, so a category group can be named. */
  categoryByItemId: Map<number, string>;
  /** True inside the phone's single horizontal bundle row (spec 19-21). */
  rail?: boolean;
};

function groupSource(group: CatalogBundleGroup): BundleSource {
  return group.source ?? group.kind ?? 'items';
}

/**
 * A short "N from X" summary for one requirement group.
 *
 * The till payload carries ids, not names, so the category name and the item
 * titles are resolved from the catalog the till already has. Hidden items are
 * eligible for a bundle but absent from that catalog, so a group made only of
 * them falls back to a generic label.
 */
function groupSummary(group: CatalogBundleGroup, itemsById: Map<number, Item>, categoryByItemId: Map<number, string>): string {
  const itemIds = Array.isArray(group.item_ids) ? group.item_ids : [];
  const source = groupSource(group);

  if (source === 'category') {
    const names = new Set<string>();
    for (const id of itemIds) {
      const name = categoryByItemId.get(id);
      if (name) names.add(name);
    }
    const label = names.size === 1 ? [...names][0] : 'a category';
    return group.quantity + ' from ' + label;
  }

  if (source === 'tag') {
    return group.quantity + ' from any tagged item';
  }

  const titles: string[] = [];
  for (const id of itemIds) {
    const title = itemsById.get(id)?.title;
    if (title && !titles.includes(title)) titles.push(title);
  }
  const label = titles.length === 0 ? 'selected items' : titles.join(' / ');
  return group.quantity + ' from ' + label;
}

/** The dearest regular total of a group's eligible units, as the cart sees it. */
function dearestTotal(group: CatalogBundleGroup, itemsById: Map<number, Item>): number {
  let high = 0;
  let known = false;
  for (const id of group.item_ids) {
    const price = itemsById.get(id)?.price;
    if (price === undefined) continue;
    high = Math.max(high, price);
    known = true;
  }
  return known ? high * group.quantity : 0;
}

const BundleCard: FC<BundleCardProps> = ({ bundle, itemsById, categoryByItemId, rail = false }) => {
  const { dispatch } = useContext(CashierContext);
  const groups = Array.isArray(bundle.groups) ? bundle.groups : [];
  const summary = groups.map((group) => groupSummary(group, itemsById, categoryByItemId)).join(' + ');

  /**
   * A tile is a shortcut only for a single SELECTED_ITEMS group: those are the
   * exact items to drop into the cart. Any other bundle is earned by picking
   * eligible items, so its tile is informational.
   */
  const single = groups.length === 1 && groupSource(groups[0]) === 'items';
  const addable = single
    ? groups[0].item_ids.length === 1
      ? [{ id: groups[0].item_ids[0], quantity: groups[0].quantity }]
      : groups[0].item_ids.map((id) => ({ id, quantity: 1 }))
    : [];
  const interactive = single && addable.length > 0;

  const regular = groups.reduce((sum, group) => sum + dearestTotal(group, itemsById), 0);
  const saving = Math.max(0, regular - bundle.price);
  const imageId = interactive ? (groups[0].item_ids[0] ?? null) : null;
  const image = imageId !== null ? (itemsById.get(imageId)?.img ?? '') : '';

  const addBundleItems = () => {
    if (!interactive) return;
    for (const entry of addable) {
      const item = itemsById.get(entry.id);
      for (let count = 0; count < entry.quantity; count += 1) {
        dispatch({
          type: 'ADD',
          payload: {
            id: entry.id,
            title: item?.title ?? '',
            price: item?.price ?? 0,
            img: item?.img ?? '',
          },
        });
      }
    }
  };

  return (
    <div
      className={rail ? 'bundleCard bundleCardRail' : 'bundleCard'}
      title={summary}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-disabled={interactive ? undefined : true}
      onClick={addBundleItems}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                addBundleItems();
              }
            }
          : undefined
      }
    >
      {/* The tile's name is the visible text beside the image, so the photo is
          decorative and must not repeat it in the accessible name. */}
      {interactive && image && <img src={resolveImageSrc(image)} alt="" />}
      <div className="itemNameAndPrice">
        <div className="name bundleName">{bundle.name}</div>
        <div className="bundleParts">{summary}</div>
        <div className="bundlePriceRow">
          <span className="price money">{formatDollars(bundle.price)}</span>
          {saving > 0 && <span className="bundleSave">save {formatDollars(saving)}</span>}
        </div>
      </div>
    </div>
  );
};

export default BundleCard;
